import { CSSProperties, HTMLAttributes } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives/responsive';

import { wacToken } from '@/libraries/design/foundations/index.ts';

import { useGenericTableVariant } from './generic-table-variant-context';

export interface GenericTableColumnHeaderProps extends HTMLAttributes<HTMLTableCellElement> {
	colWidth?: string;
	/**
	 * v2 only: this is the header of the row title column, so it stays in place on mobile with
	 * the row titles. (v1 always keeps the first column header in place.)
	 */
	isRowHeaderColumn?: boolean;
}

const headCellCss = css({
	fontWeight: 700,
	textAlign: 'left',
	color: wacToken('color.primary.black'),
	backgroundColor: wacToken('color.neutral.light0'),
	display: 'table-cell',
	padding: '14px',
	borderInlineEnd: `1px solid ${wacToken('color.neutral.light400')}`,
	':last-of-type': {
		borderInlineEnd: 'none',
	},
	width: 'var(--generic-table-column-header-width)',
	[media.below.sm]: {
		borderInlineEnd: `1px solid ${wacToken('color.neutral.light400')}`,
		borderInlineStart: 0,
		maxWidth: 'var(--generic-table-mobile-column-width)',
		minWidth: 'var(--generic-table-mobile-column-width)',
		width: 'var(--generic-table-mobile-column-width)',
		':first-of-type': {
			borderInlineEnd: 0,
			insetInlineStart: 0,
			position: 'sticky',
			zIndex: 3,
		},
		':first-of-type::after': {
			backgroundColor: wacToken('color.neutral.light400'),
			content: '""',
			insetBlock: 0,
			insetInlineEnd: 0,
			pointerEvents: 'none',
			position: 'absolute',
			width: '1px',
		},
	},
});

const V2_DIVIDER = `1px solid ${wacToken('color.neutral.light300')}`;

const headCellV2Css = css({
	backgroundColor: wacToken('color.extended.blue100'),
	borderInlineEnd: V2_DIVIDER,
	[media.below.sm]: {
		borderInlineEnd: V2_DIVIDER,
	},
});

// v2: only the row title column's header stays in place on mobile; any other first column
// header scrolls with the others, like the cells below it
const firstColumnNotStickyV2Css = css({
	[media.below.sm]: {
		':first-of-type': {
			borderInlineEnd: V2_DIVIDER,
			position: 'static',
			zIndex: 'auto',
		},
		':first-of-type::after': {
			content: 'none',
		},
	},
});

type GenericTableColumnHeaderStyle = CSSProperties & {
	'--generic-table-column-header-width': string | number;
};

export const GenericTableColumnHeader = ({
	colWidth,
	isRowHeaderColumn,
	style,
	...props
}: GenericTableColumnHeaderProps) => {
	const { width, ...restStyle } = style ?? {};
	const { isV2 } = useGenericTableVariant();

	return (
		<th
			{...props}
			css={[
				headCellCss,
				isV2 && headCellV2Css,
				isV2 && !isRowHeaderColumn && firstColumnNotStickyV2Css,
			]}
			scope="col"
			style={
				{
					'--generic-table-column-header-width':
						colWidth ?? width ?? 'var(--generic-table-col-width)',
					...restStyle,
				} as GenericTableColumnHeaderStyle
			}
		/>
	);
};
